import flatten from 'lodash/flatten';
import { FIELD_NAMES, FIELD_TYPES, SF_TYPES, BACKUP_CONSENT_FIELD_NAMES, GDPR_SETTING } from 'utils/constants';
import { createDefaultUIStateForType } from 'utils/editorDefaultStates';
import { convertDropdownValueToKey, convertMultipicklistValueToKey } from 'utils/idUtils';
import { filterAnswersByActive } from 'utils/branchingUtils';
import Graph from 'utils/conditionality.graph';
import {
  ERROR_TEXT_LEAD_SHARE_WITH_GDPR_CONSENT,
  ERROR_TEXT_LEAD_SHARE_WITH_US_CONSENT,
  ERROR_TEXT_NO_FIELD_FIRST_NAME,
  ERROR_TEXT_NO_FIELD_LAST_NAME,
  ERROR_TEXT_NO_FIELD_EMAIL,
  ERROR_TEXT_NO_CONSENT_GDPR,
  ERROR_TEXT_NO_CONSENT_GEO,
  ERROR_TEXT_NO_CONSENT_US,
} from 'langs/en/errors';

/**
 * Builds dropdown configuration from field data and Salesforce picklist values
 * @param {Object} fieldData - The field data object
 * @param {Object} sfData - The Salesforce data object containing sfPicklistValues
 * @returns {Array} Array containing [formedDropdownConfig, dropdownEditorUiState]
 */
export function buildDropdownConfig(fieldData, sfData) {
  const formedPicklistValues = {};
  const optionsOrder = [];
  const dropdownEditorUiState = createDefaultUIStateForType(FIELD_TYPES.DROPDOWN, fieldData.hidden);

  sfData.sfPicklistValues.forEach((el) => {
    const valueConcatenated = convertDropdownValueToKey(el.value);
    formedPicklistValues[valueConcatenated] = {
      label: el.label,
      value: el.value,
      shouldDisplay: true,
    };
    optionsOrder.push(valueConcatenated);
    dropdownEditorUiState.dropdownConfig.options[valueConcatenated] = {};
  });

  const formedDropdownConfig = {
    dropdownConfig: {
      options: formedPicklistValues,
      defaultOption: '',
      optionsOrder,
    },
    editorType: FIELD_TYPES.DROPDOWN,
  };

  return [
    formedDropdownConfig,
    dropdownEditorUiState,
  ];
}

/**
 * Builds multipicklist configuration from field data and Salesforce picklist values
 * @param {Object} fieldData - The field data object
 * @param {Object} sfData - The Salesforce data object containing sfPicklistValues
 * @returns {Array} Array containing [formedMultipicklistConfig, multipicklistEditorUiState]
 */
export function buildMultipicklistConfig(fieldData, sfData) {
  const formedPicklistValues = {};
  const optionsOrder = [];
  const multipicklistEditorUiState = createDefaultUIStateForType(FIELD_TYPES.MULTIPICKLIST, fieldData.hidden);

  sfData.sfPicklistValues.forEach((el) => {
    const valueConcatenated = convertMultipicklistValueToKey(el.value);
    formedPicklistValues[valueConcatenated] = {
      label: el.label,
      value: el.value,
      shouldDisplay: true,
    };
    optionsOrder.push(valueConcatenated);
    multipicklistEditorUiState.multipicklistConfig.options[valueConcatenated] = {};
  });

  const formedMultipicklistConfig = {
    multipicklistConfig: {
      options: formedPicklistValues,
      defaultOption: '',
      optionsOrder,
    },
    editorType: FIELD_TYPES.MULTIPICKLIST,
  };

  return [
    formedMultipicklistConfig,
    multipicklistEditorUiState,
  ];
}

/**
 * Derives the field type from a field name, checking both standard field names and Salesforce data
 * @param {string} fieldName - The name of the field
 * @param {Object} sfData - The Salesforce data object containing sfFieldType
 * @returns {string} The derived field type constant
 */
export function deriveFieldTypeFromName(fieldName, sfData) {
  const fieldNameToTypeMap = {
    [FIELD_NAMES.CONSENT_GDPR]: FIELD_TYPES.GDPR_CONSENT,
    [FIELD_NAMES.CONSENT_NON_GDPR]: FIELD_TYPES.NON_GDPR_CONSENT,
    [FIELD_NAMES.COUNTRY]: FIELD_TYPES.COUNTRY,
    [FIELD_NAMES.COUNTRY_OF_CITIZENSHIP]: FIELD_TYPES.COUNTRY,
    [FIELD_NAMES.CP_DEGREE_OFFERING]: FIELD_TYPES.DEGREE_OFFERING,
    [FIELD_NAMES.NATIONALITY]: FIELD_TYPES.NATIONALITY,
    [FIELD_NAMES.STATE]: FIELD_TYPES.STATE,
    [FIELD_NAMES.LEAD_SHARE_OPT_IN]: FIELD_TYPES.PICKLIST_VALUE_OPT_IN,
    [FIELD_NAMES.EMAIL]: FIELD_TYPES.EMAIL,
    [FIELD_NAMES.PHONE]: FIELD_TYPES.PHONE,
    [FIELD_NAMES.GDPR_PROSPECT_2U_OPT_IN]: FIELD_TYPES.BOOLEAN_OPT_IN,
    [FIELD_NAMES.GDPR_PROSPECTPARTNER_OPT_IN]: FIELD_TYPES.BOOLEAN_OPT_IN,
  };
  if (fieldNameToTypeMap[fieldName]) return fieldNameToTypeMap[fieldName];

  const { sfFieldType } = sfData;
  if (sfFieldType === SF_TYPES.PICKLIST) return FIELD_TYPES.DROPDOWN;
  if (sfFieldType === SF_TYPES.BOOLEAN) return FIELD_TYPES.RADIO_BUTTON;
  if (sfFieldType === SF_TYPES.MULTIPICKLIST) return FIELD_TYPES.MULTIPICKLIST;

  return FIELD_TYPES.TEXT;
}

/**
 * Builds radio button configuration
 * @returns {Object} Radio button configuration object
 */
export const buildRadioButtonConfig = () => ({
  radioButtonConfig: {
    options: {
      true: {
        label: 'Yes',
        value: 'true',
        shouldDisplay: true,
      },
      false: {
        label: 'No',
        value: 'false',
        shouldDisplay: true,
      },
    },
    optionsOrder: ['true', 'false'],
    defaultOption: '',
    layout: 'vertical',
  },
});

/**
 * Uses conditions to determine invalid form state. If no conditions are met, form is valid
 *
 * @param {object} form - entire form redux state at save time
 * @returns - {isInvalid: boolean, message: String}
 */
export function hasInvalidFormState(form) {
  const retval = { isInvalid: false, message: '' };
  const { fields, fieldConditionalityInfo: { dehydratedGraph, branchingFields }, formMetadata } = form;
  const areAllFieldsOptional = Boolean(formMetadata?.areAllFieldsOptional);

  const graph = new Graph(dehydratedGraph);
  const verticiesBySN = Object.keys(fields).reduce((memo, id) => {
    const vertex = graph.findOrCreateVertex({ left: id });
    if (fields[id].delete) {
      return memo;
    }
    if (memo[fields[id].name]) {
      memo[fields[id].name].push(vertex);
    } else {
      memo[fields[id].name] = [vertex]; // eslint-disable-line no-param-reassign
    }
    return memo;
  }, {});

  const gdprConsentVertexList = verticiesBySN[FIELD_NAMES.CONSENT_GDPR];
  const usConsentVertexList = verticiesBySN[FIELD_NAMES.CONSENT_NON_GDPR];
  const leadShareOptInVertexList = verticiesBySN[FIELD_NAMES.LEAD_SHARE_OPT_IN];
  if (!areAllFieldsOptional) {
    const activeVerticies = flatten(
      graph.getUserBranchingFields().map((fieldId) => filterAnswersByActive(branchingFields[fieldId], fields[fieldId])
        .map((value) => graph.findOrCreateVertex({ left: fieldId, op: 'EQ', right: value })))
    );
    const backupConsentVerticies = BACKUP_CONSENT_FIELD_NAMES.reduce((memo, name) => {
      if (verticiesBySN[name]) {
        memo.push([verticiesBySN[name]]);
      }
      return memo;
    }, []);
    const firstNameVertexList = verticiesBySN[FIELD_NAMES.FIRST_NAME];
    const lastNameVertexList = verticiesBySN[FIELD_NAMES.LAST_NAME];
    const emailVertexList = verticiesBySN[FIELD_NAMES.EMAIL];

    switch (form.syntheticContentEntities.gdprSetting) {
      case GDPR_SETTING.ALWAYS: {
        const gdprConsentVertices = [[gdprConsentVertexList], ...backupConsentVerticies];
        if (!graph.ensureAtLeastOneReachable(gdprConsentVertices, activeVerticies)) {
          retval.isInvalid = true;
          retval.message = ERROR_TEXT_NO_CONSENT_GDPR;
          return retval;
        }
        break;
      }
      case GDPR_SETTING.GEO: {
        const geoConsentVertices = [[gdprConsentVertexList, usConsentVertexList], ...backupConsentVerticies];
        if (!graph.ensureAtLeastOneReachable(geoConsentVertices, activeVerticies)) {
          retval.isInvalid = true;
          retval.message = ERROR_TEXT_NO_CONSENT_GEO;
          return retval;
        }
        break;
      }
      case GDPR_SETTING.NEVER: {
        const usConsentVertices = [[leadShareOptInVertexList], [usConsentVertexList], ...backupConsentVerticies];
        if (!graph.ensureAtLeastOneReachable(usConsentVertices, activeVerticies)) {
          retval.isInvalid = true;
          retval.message = ERROR_TEXT_NO_CONSENT_US;
          return retval;
        }
        break;
      }
      case GDPR_SETTING.ALWAYS_IMPLIED: // All requirements are on individual fields
      case GDPR_SETTING.GEO_IMPLIED: // All requirements are on individual fields with geo variants
      default:
    }
    if (!firstNameVertexList || !graph.isVertexGroupAlwaysReachable(firstNameVertexList, activeVerticies)) {
      retval.isInvalid = true;
      retval.message = ERROR_TEXT_NO_FIELD_FIRST_NAME;
      return retval;
    }
    if (!lastNameVertexList || !graph.isVertexGroupAlwaysReachable(lastNameVertexList, activeVerticies)) {
      retval.isInvalid = true;
      retval.message = ERROR_TEXT_NO_FIELD_LAST_NAME;
      return retval;
    }
    if (!emailVertexList || !graph.isVertexGroupAlwaysReachable(emailVertexList, activeVerticies)) {
      retval.isInvalid = true;
      retval.message = ERROR_TEXT_NO_FIELD_EMAIL;
      return retval;
    }
  }

  const standardNames = Object.keys(verticiesBySN);
  const rootedVerticies = [
    0,
    graph.findOrCreateVertex({ left: 'state.no_klondike_gdpr_setting', op: 'EQ', right: 'geo' }),
    graph.findOrCreateVertex({ left: 'state.no_klondike_gdpr_setting', op: 'EQ', right: 'never' }),
    graph.findOrCreateVertex({ left: 'state.no_klondike_gdpr_setting', op: 'EQ', right: 'always' }),
    graph.findOrCreateVertex({ left: 'state.no_klondike_carmen_sandiego_region', op: 'EQ', right: 'eu' }),
    graph.findOrCreateVertex({ left: 'state.no_klondike_carmen_sandiego_region', op: 'NE', right: 'eu' }),
  ];

  for (let i = 0; i < standardNames.length; i++) {
    const snCheck = graph.areGroupMembersUniquelyReachable(verticiesBySN[standardNames[i]], rootedVerticies);
    if (!snCheck.verticiesUniquelyReachable) {
      retval.isInvalid = true;
      if (snCheck.multiplyReachable[0]) {
        if (snCheck.multiplyReachable[0].length > 1) {
          retval.message = `${fields[graph.getVertexDetails(snCheck.multiplyReachable[0][0]).left].name} appears multiple times on all branches`;
        } else {
          retval.message = `${fields[graph.getVertexDetails(snCheck.multiplyReachable[0][0]).left].name} is on all branches, but also appears on specific branches`;
        }
      } else {
        const branches = Object.keys(snCheck.multiplyReachable);
        retval.message = `${fields[graph.getVertexDetails(snCheck.multiplyReachable[branches[0]][0]).left].name} appears multiple times on branch with answer "${graph.getVertexDetails(+branches[0]).right}"`;
      }
      return retval;
    }
  }

  const tyRoutingVertices = Object.keys(form.thankYouRoutingFields)
    // First part of this filter catches a case we ran into where Object.keys was returning removed keys that had value = undefined
    .filter((fieldId) => (form.thankYouRoutingFields[fieldId] && !fields[fieldId].delete))
    .map((id) => graph.findOrCreateVertex({ left: id }));
  const tyCheck = graph.areGroupMembersUniquelyReachable(tyRoutingVertices, rootedVerticies);
  if (!tyCheck.verticiesUniquelyReachable) {
    retval.isInvalid = true;
    if (tyCheck.multiplyReachable[0]) {
      const invaldTYRoutingFieldNames = tyCheck.multiplyReachable[0]
        .map((vertexId) => fields[graph.getVertexDetails(vertexId).left].name)
        .join(', ');
      if (tyCheck.multiplyReachable[0].length > 1) {
        retval.message = `Multiple Thank You Routing questions found on all branches: ${invaldTYRoutingFieldNames}`;
      } else {
        retval.message = `Thank You Routing question ${invaldTYRoutingFieldNames} is on all branches, but specific branches also have Thank You Routing`;
      }
    } else {
      const branches = Object.keys(tyCheck.multiplyReachable);
      const invaldTYRoutingFieldNames = tyCheck.multiplyReachable[branches[0]]
        .map((vertexId) => fields[graph.getVertexDetails(vertexId).left].name)
        .join(', ');
      retval.message = `Multiple Thank You Routing Questions ${invaldTYRoutingFieldNames} appear together on branch with answer "${graph.getVertexDetails(+branches[0]).right}"`;
    }
    return retval;
  }

  const leadShareAndGDPRVertexArray = [leadShareOptInVertexList, gdprConsentVertexList].reduce((memo, vertexList) => {
    if (vertexList) memo.push(...vertexList);
    return memo;
  }, []);
  const shareAndGDPRCheck = graph.areGroupMembersUniquelyReachable(leadShareAndGDPRVertexArray);
  if (!shareAndGDPRCheck.verticiesUniquelyReachable) {
    retval.isInvalid = true;
    retval.message = ERROR_TEXT_LEAD_SHARE_WITH_GDPR_CONSENT;
    return retval;
  }

  const leadShareAndUSVertexArray = [leadShareOptInVertexList, usConsentVertexList].reduce((memo, vertexList) => {
    if (vertexList) memo.push(...vertexList);
    return memo;
  }, []);
  const shareAndUSCheck = graph.areGroupMembersUniquelyReachable(leadShareAndUSVertexArray);
  if (!shareAndUSCheck.verticiesUniquelyReachable) {
    retval.isInvalid = true;
    retval.message = ERROR_TEXT_LEAD_SHARE_WITH_US_CONSENT;
    return retval;
  }

  // Form is valid
  return retval;
}
